import type { TranslationFunction } from '../../translations/Translation';
import { getOptionKey } from './roomCommon';
import { getPromotionLabel } from './promotionLabel';

// Shared cancellation-policy grouping logic used by both the desktop cancellation modal
// (CancellationPolicyModal) and the EVO mobile explainer (EvoRoomCancellationInformation).
// The two render the result with different markup/styling, but the data grouping must stay
// identical — keep it here so the two presentations cannot drift apart.

// Complex Example

// Mealplan ACCOMMODATION_ONLY 1 -  Cancellation Fees[
//   { "range": { "from": "2024-08-01", "to": "2024-12-06" }, "type": "NOT_REFUNDABLE", "amount": 169.77 }
// ]
// Mealplan BED_AND_BREAKFAST 1 -  Cancellation Fees[
//   { "range": { "from": "2024-08-01", "to": "2024-12-06" }, "type": "NOT_REFUNDABLE", "amount": 207.09 }
// ]
// Mealplan ACCOMMODATION_ONLY 2 -  Cancellation Fees[
//   { "range": { "from": "2024-08-01", "to": "2024-12-04" }, "type": "FREE_CANCELLATION", "amount": 0 },
//   { "range": { "from": "2024-12-05", "to": "2024-12-06" }, "type": "NOT_REFUNDABLE", "amount": 199.72 }
// ]
// Mealplan BED AND BREAKFAST 2 -  Cancellation Fees[
//   { "range": { "from": "2024-08-01", "to": "2024-12-05" }, "type": "FREE_CANCELLATION", "amount": 0 },
//   { "range": { "from": "2024-12-06", "to": "2024-12-06" }, "type": "NOT_REFUNDABLE", "amount": 230.1 }
// ]

// The meal plans with only NOT_REFUNDABLE can be grouped because range dates are the same.

// The meal plans with FREE_CANCELLATION + NOT_REFUNDABLE cant be grouped because range dates are different,
// we need to show different dates ranges in the Modal. This is why we need another level of grouping in the
// CancellationPoliciesByRange.

export type CancellationPoliciesByRange = {
  // key containing all the ranges, from free cancellation to partial cancellation to not refundable.
  [key: string]: {
    // key containing a specific range
    [key: string]: {
      range: { from: string; to: string };
      cancellationPolicies: { boardClass: string; discount?: string; amount?: number }[];
      type: CancellationInfoType;
    };
  };
};

export const cancellationPoliciesTranslationKeys = {
  NOT_REFUNDABLE: {
    titleHO: 'vacation.common.mealplan.room.cancellationtype.nonrefundable.title',
    titleDP: 'vacation.common.mealplan.room.cancellationtype.nonrefundable.title',
    description: 'vacation.common.mealplan.room.cancellationtype.nonrefundable.description',
    withoutMessages: 'vacation.common.mealplan.room.cancellationtype.nonrefundable.withoutlist',
  },
  REFUNDABLE: {
    titleHO: 'vacation.common.mealplan.room.cancellationtype.refundable.title.ho',
    titleDP: 'vacation.common.mealplan.room.cancellationtype.refundable.title.dp',
    description: 'vacation.common.mealplan.room.cancellationtype.refundable.description',
    withoutMessages: 'vacation.common.mealplan.room.cancellationtype.refundable.withoutlist',
  },
};

// Group refundable meal plans by their free-cancellation `upTo` date so each distinct date
// renders in its own block.
export function groupRefundableByUpToDate(refundable: MealPlanType[]): MealPlanType[][] {
  const byDate = refundable.reduce(
    (acc, obj) => {
      const upToDate = obj.cancellationPolicy.upTo || 'default';
      if (!acc[upToDate]) {
        acc[upToDate] = [];
      }
      acc[upToDate].push(obj);
      return acc;
    },
    {} as { [key: string]: MealPlanType[] }
  );

  // Order the blocks by their free-cancellation `upTo` date (ascending). Dates are in
  // SHOPPING_API_DATE_FORMAT (yyyy-MM-dd), which sorts chronologically as plain strings;
  // groups with no date (keyed 'default') go last.
  return Object.values(byDate).sort((groupA, groupB) => {
    const dateA = groupA[0]?.cancellationPolicy.upTo;
    const dateB = groupB[0]?.cancellationPolicy.upTo;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
  });
}

// Build the two-level range grouping for a set of meal plans of the same cancellation type.
// When two meal plans share the same range, board class and discount, keep only the cheapest
// one to avoid duplicate rows (and duplicate React keys) when rendered.
// The promotion labeler is caller-picked: the Evo page shows benefit labels with the
// result-page copy (MPH-721) while the classic modal keeps the provider label — the grouping
// itself must stay identical for both.
export function buildCancellationPoliciesByRange(
  mealPlans: MealPlanType[],
  t: TranslationFunction,
  accommodationOnly: boolean,
  getLabel: (promotion: MealPlanPromotion, t: TranslationFunction) => string = getPromotionLabel
): CancellationPoliciesByRange {
  return mealPlans.reduce((acc: CancellationPoliciesByRange, mealPlan) => {
    if (!mealPlan.cancellationFees) return acc;
    const mealPlanRangesKey = mealPlan.cancellationFees.reduce(
      (rangesKey, fee) => rangesKey.concat(`${fee.range.from}-${fee.range.to},`),
      ''
    );
    mealPlan.cancellationFees?.forEach((cancellationFee) => {
      if (cancellationFee.range.from) {
        // Create the outer group only when a fee actually lands in it: a meal plan with an
        // empty cancellationFees array (or fees without a range) must not leave an empty
        // group behind — consumers index into each group's first entry and would crash.
        if (!acc[mealPlanRangesKey]) {
          acc[mealPlanRangesKey] = {};
        }
        const key = `${cancellationFee.range.from}-${cancellationFee.range.to}`;
        if (!acc[mealPlanRangesKey][key]) {
          acc[mealPlanRangesKey][key] = {
            range: cancellationFee.range,
            type: cancellationFee.type,
            cancellationPolicies: [],
          };
        }
        const policies = acc[mealPlanRangesKey][key].cancellationPolicies;
        const boardClass = t(getOptionKey(mealPlan.boardClass, accommodationOnly));
        const discount = mealPlan.promotions?.map((promotion) => getLabel(promotion, t)).join(', ');
        const { amount } = cancellationFee;
        const policyKey = `${boardClass}${discount ? `-${discount}` : ''}`;
        // Two meal plans can share the same range, board class and discount, which would
        // produce duplicate keys when rendered. Keep only the cheapest one.
        const existing = policies.find(
          (policy) => `${policy.boardClass}${policy.discount ? `-${policy.discount}` : ''}` === policyKey
        );
        if (existing) {
          if (amount !== undefined && (existing.amount === undefined || amount < existing.amount)) {
            existing.amount = amount;
          }
        } else {
          policies.push({ boardClass, discount, amount });
        }
      }
    });
    return acc;
  }, {});
}
